//src/utils/renderInput.tsx
import { store } from 'app/redux/store';
import { useGetSpecializationsQuery } from 'app/services/accountant';
import { useGetUserBusinessesQuery } from 'app/services/user';
import { CheckboxInput } from 'Components/Auth/CheckboxInput';
import { MultipleSelectInput } from 'Components/Auth/MultipleSelectInput';
import {
  SelectInput,
  SelectInputProps,
} from 'Components/Auth/SelectInput/index';
import { TextInput, TextInputProps } from 'Components/Auth/TextInput';
import { TextAreaInput } from 'Components/Documents/TextAreaInput';
import BaseInput from 'Components/Form/BaseInput';
import DateInput, { DateRangesInput } from 'Components/Form/DateInput';
import FieldArrayInput from 'Components/Form/FieldArrayInput';
import Information, { IInformationProps } from 'Components/Form/Information';
import { MaskedInput } from 'Components/Form/MaskedInput';
import QuillInput from 'Components/Form/QuillInput';
import { RadioInput } from 'Components/Form/RadioInput';
import { UploadInput } from 'Components/Form/UploadInput';
import { countries } from 'countries';
import lodashGet from 'lodash.get';
import { ReactElement } from 'react';
import { states } from 'states';

export default function renderInput(
  input: IBaseField<any>,
): ReactElement | null {
  const { type } = input;
  const hideDependency = (() => {
    if (input.dependentOn && input.predicate) {
      // get the value of the key that the upload field depends on and check it against the predicate function
      const value = lodashGet(store.getState(), input.dependentOn);
      if (!value) {
        return false;
      }
      return !input.predicate(value);
    }
  })();

  if (hideDependency) {
    return null;
  }

  switch (type) {
    case 'select':
      return <SelectInput {...(input as ISelectField)} />;
    case 'country':
      return (
        <SelectInput {...(input as ISelectField)} options={[...countries]} />
      );

    case 'multi-country':
      return (
        <MultipleSelectInput
          {...(input as ISelectField)}
          options={[...countries]}
        />
      );

    case 'state':
      return <SelectInput {...(input as ISelectField)} options={[...states]} />;

    case 'multi-state':
      return (
        <MultipleSelectInput
          {...(input as ISelectField)}
          options={[...states]}
        />
      );

    case 'multiSelect':
      return <MultipleSelectInput {...(input as ISelectField)} />;
    case 'radio':
      return <RadioInput {...(input as IRadioField)} />;
    case 'text':
    case 'currency':
      return <TextInput {...(input as ITextField)} />;
    case 'upload':
      return <UploadInput {...(input as IUploadField)} />;
    case 'date':
      return <DateInput {...(input as IDateField)} />;
    case 'checkbox':
      return <CheckboxInput {...(input as ICheckboxField)} />;
    case 'textarea':
      return <TextAreaInput {...(input as ITextAreaField)} />;
    case 'fieldArray':
      return <FieldArrayInput {...(input as IFieldArrayField)} />;
    case 'maskedInput':
      return <MaskedInput {...(input as IMaskedField)} />;
    case 'wysiwyg':
      return <QuillInput {...(input as IQuillField)} />;
    case 'information':
      return <Information {...(input as IInformationProps)} />;
    case 'number':
      return <TextInput {...(input as IInformationProps)} type="number" />;
    case 'specialization':
      return <SelectSpecializationInput {...(input as ISelectField)} />;

    case 'dateRanges':
      return <DateRangesInput {...(input as IDateRangesField)} />;

    case 'business':
      return <SelectBusinessInput {...(input as SelectInputProps)} />;

    default:
      return null;
  }
}

export function NumberInput({
  name,
  placeholder = 'Enter...',
  rules,
  autoComplete = 'on',
  disable,
  autoFocus,
  ...rest
}: Omit<TextInputProps, 'type'>) {
  return (
    <BaseInput name={name} {...rest}>
      {({ register }) => (
        <>
          <input
            {...register(name, rules)}
            autoFocus={autoFocus}
            className={`form__input`}
            placeholder={placeholder}
            disabled={disable}
            autoComplete={autoComplete}
            inputMode={'numeric'}
          />
        </>
      )}
    </BaseInput>
  );
}

export function SelectBusinessInput(input: SelectInputProps) {
  const { data: businesses } = useGetUserBusinessesQuery();

  return (
    <SelectInput
      {...input}
      options={
        (businesses || []).map((b) => ({
          label: b.name,
          value: b.id.toString(),
        })) || []
      }
    />
  );
}

export function SelectSpecializationInput(input: SelectInputProps) {
  const { data: specializations } = useGetSpecializationsQuery();

  return <SelectInput {...input} options={specializations || []} />;
}
